import React, { useEffect, useMemo, useState } from 'react';
import { NewAdminPanel } from '@/components/SuperAdminPanel';
import { useApp } from '@/pages/providers/AppProvider';
import { useAuth } from '@/components/auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/lib/api-client';
import { toast } from 'sonner';
import { useConfirmDialog } from '@/components/ui/confirm-dialog';

import Loader from '@/components/ui/Loader';

export function SuperAdminPanel() {
  const {
    jobs,
    users,
    applications,
    companies,
    currentUser,
    setUsers,
    setCompanies,
  } = useApp();

  // Local admin jobs state driven by server, independent from global site jobs
  const [adminJobs, setAdminJobs] = useState<any[]>([]);
  const [adminApplications, setAdminApplications] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Normalize jobs: ensure there's an `id` for diffing and UI
  const normalizeJob = (j: any) => {
    const toNum = (v: any) => {
      const n = parseInt(String(v ?? '').replace(/[^0-9]/g, ''));
      return Number.isFinite(n) ? n : null;
    };
    const currency = String(j.salary_currency || 'NPR');
    const nmin = toNum((j as any).salary_min);
    const nmax = toNum((j as any).salary_max);
    let salaryStr = '';
    const sType = String((j as any).salary_type || (j as any).salaryType || '').trim().toLowerCase();
    if (nmin && nmax) {
      salaryStr = `${currency} ${nmin.toLocaleString()} - ${nmax.toLocaleString()}`;
    } else {
      const s = String((j as any).salary_range || (j as any).salary || '').trim();
      salaryStr = s;
      if (!salaryStr && (sType === 'negotiable' || sType.includes('negotiable'))) {
        const pay = (j as any).pay_type || (j as any).payType;
        salaryStr = pay ? `Negotiable (${pay})` : 'Negotiable';
      }
      if (!salaryStr) {
        // Default to Negotiable when no explicit range or formatted value
        const pay = (j as any).pay_type || (j as any).payType;
        salaryStr = pay ? `Negotiable (${pay})` : 'Negotiable';
      }
    }

    return {
      id: j.id || j._id,
      ...j,
      // Bridge backend `approval_status` to frontend `approvalStatus`
      approvalStatus: j.approvalStatus || j.approval_status || (
        j.status === 'active' ? 'approved' : j.status === 'rejected' ? 'rejected' : 'pending'
      ),
      // Unified cover image field
      coverImageUrl: (j as any).coverImageUrl || (j as any).cover_image_url || undefined,
      // Ensure admin UI sees salary string consistently
      salary: salaryStr,
      salary_range: (j as any).salary_range || (nmin && nmax ? salaryStr : salaryStr)
    };
  };

  // Initial load of admin jobs, applications, companies, and users from backend
  useEffect(() => {
    (async () => {
      try {
        const [jobsRes, appsRes, usersRes, companiesRes] = await Promise.allSettled([
          apiClient.getAdminJobs({ page: 1, limit: 2000 }),
          apiClient.getAdminApplications({ limit: 2000 }),
          apiClient.getAllUsers({ limit: 2000 }),
          apiClient.getAdminCompanies({ page: 1, limit: 2000 })
        ]);

        // Handle Jobs
        if (jobsRes.status === 'fulfilled') {
          const list = Array.isArray(jobsRes.value?.jobs) ? jobsRes.value.jobs : (Array.isArray(jobsRes.value) ? jobsRes.value : []);
          setAdminJobs(list.map(normalizeJob));
        } else {
          setAdminJobs((jobs || []).map(normalizeJob));
        }

        // Handle Applications
        if (appsRes.status === 'fulfilled') {
          const items = Array.isArray((appsRes.value as any)?.applications) ? (appsRes.value as any).applications : (Array.isArray(appsRes.value) ? (appsRes.value as any) : []);
          const mapped = items.map((a: any) => ({
            id: a._id || a.id,
            jobId: a.job_id || a.jobId,
            userId: a.job_seeker_id || a.userId,
            status: a.status || 'pending',
            appliedAt: a.applied_at || a.appliedAt,
          }));
          setAdminApplications(mapped);
        } else {
          setAdminApplications([]);
        }

        // Handle Users
        if (usersRes.status === 'fulfilled') {
          const res: any = usersRes.value;
          const items: any[] = Array.isArray(res?.users) ? res.users : (Array.isArray(res) ? res : []);
          const mapped = items.map((u: any) => ({
            id: u._id || u.id || String(Date.now()),
            name: u.full_name || u.name || '',
            email: u.email || '',
            user_type: (u.user_type || u.type || '').toLowerCase(),
            type: (u.user_type === 'job_seeker' ? 'jobseeker' : (u.user_type || u.type || '')).toLowerCase(),
            company: u.company_name || u.company || '',
            isVerified: !!(u.isVerified ?? u.is_verified ?? u.verified ?? (u.profile && (u.profile.isVerified ?? u.profile.is_verified ?? u.profile.verified)) ?? false),
            profile: u.profile || {}
          }));
          setUsers(mapped);
        }

        // Handle Companies
        if (companiesRes.status === 'fulfilled') {
          const res: any = companiesRes.value;
          const list = Array.isArray(res) ? res : (Array.isArray(res?.companies) ? res.companies : []);
          setCompanies(list);
        }

      } catch (err) {
        console.error("Dashboard loading error", err);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!adminJobs || adminJobs.length === 0) return;
    const countByJob: Record<string, number> = {};
    for (const a of adminApplications) {
      const jid = String((a as any).jobId || '');
      if (!jid) continue;
      countByJob[jid] = (countByJob[jid] ?? 0) + 1;
    }
    setAdminJobs(prev => prev.map(j => {
      const jid = String((j as any).id || '');
      const cnt = countByJob[jid] ?? 0;
      return { ...j, applicationCount: cnt };
    }));
  }, [adminApplications]);

  useEffect(() => {
    const onMessage = async (ev: MessageEvent) => {
      try {
        const evt = JSON.parse(ev.data);
        if (evt?.topic && (evt.topic === 'jobs.created' || evt.topic === 'jobs.updated' || evt.topic === 'jobs.deleted')) {
          try {
            const res = await apiClient.getAdminJobs({ page: 1, limit: 2000 });
            const list = Array.isArray(res?.jobs) ? res.jobs : (Array.isArray(res) ? res : []);
            setAdminJobs(list.map(normalizeJob));
          } catch { }
        }
      } catch { }
    };

    const controller = apiClient.subscribeRealtimePersistent([
      'jobs.created',
      'jobs.updated',
      'jobs.deleted'
    ], onMessage as any);

    return () => { try { controller && controller.stop(); } catch { } };
  }, []);

  const { logout } = useAuth();
  const navigate = useNavigate();
  const { confirm, ConfirmDialog } = useConfirmDialog();



  const handleBackToWebsite = () => {
    navigate('/');
  };

  const handleLogout = async () => {
    const confirmed = await confirm({
      title: 'Logout from Super Admin?',
      description: 'You will be signed out and redirected to the login page.',
      type: 'warning',
      confirmText: 'Logout',
      cancelText: 'Cancel',
    });
    if (!confirmed) return;
    try {
      await logout();
    } catch (err) {
      // ignore errors in logout for now
    }
    navigate('/super-admin/login');
  };

  // Persist companies and update global state
  const handleCompanyUpdate = (nextCompanies: any[]) => {
    setCompanies(nextCompanies);
    // Stop persisting companies to server-side section settings; rely on DB only
  };

  // Persist admin jobs via backend and refresh from server
  const handleJobUpdate = async (nextJobs: any[]) => {
    const oldJobs = adminJobs;
    setAdminJobs(nextJobs.map(normalizeJob));
    try {
      const oldMap = new Map(oldJobs.map((j: any) => [j.id || j._id, j]));
      const nextMap = new Map(nextJobs.map((j: any) => [j.id || j._id, j]));

      const created = Array.from(nextMap.keys())
        .filter((id) => !oldMap.has(id))
        .map((id) => nextMap.get(id));
      const deleted = Array.from(oldMap.keys())
        .filter((id) => !nextMap.has(id))
        .map((id) => oldMap.get(id));
      const updated = Array.from(nextMap.keys())
        .filter((id) => oldMap.has(id) && JSON.stringify(oldMap.get(id)) !== JSON.stringify(nextMap.get(id)))
        .map((id) => ({ before: oldMap.get(id), after: nextMap.get(id) }));

      const calls: Promise<any>[] = [];

      for (const job of created) {
        // Skip creating if the job already has a persisted identifier
        // Jobs created through JobPostManagement post to the backend first
        // and then call onJobUpdate with the returned _id/id, so re-creating causes duplicates.
        if (job?._id || job?.id) {
          continue;
        }
        const companyObj = companies.find((c: any) => c.name === job.company);
        const reqPayload = Array.isArray(job.requirements)
          ? job.requirements
          : (typeof job.requirements === 'string'
            ? job.requirements.split('\n').map((r: string) => r.trim()).filter(Boolean)
            : undefined);
        const payload: any = {
          title: job.title,
          job_title: job.title,
          description: job.description,
          job_description: job.description,
          requirements: reqPayload,
          location: job.location,
          job_location: job.location,
          category_id: job.category_id || undefined,
          company_id: companyObj?._id || companyObj?.id,
          company: job.company,
          employment_type: job.employment_type,
          type: job.employment_type,
          experience_level: job.experience_level || job.experience,
          experience: job.experience_level || job.experience,
          tier: job.tier || job.type,
          featured: job.featured,
          license_required: !!(job.license_required ?? job.licenseRequired),
          deadline: job.deadline,
          // Super admin-created jobs should be live unless explicitly set otherwise
          status: job.status || 'active'
        };
        calls.push(apiClient.createJobByAdmin(payload));
      }

      for (const job of deleted) {
        const jid = job.id || job._id;
        if (jid) calls.push(apiClient.deleteJobByAdmin(jid));
      }

      for (const change of updated) {
        const before = change.before;
        const after = change.after;
        const approvalChanged = before.approvalStatus !== after.approvalStatus;
        const statusChanged = before.status !== after.status;
        const tierChanged = before.tier !== after.tier;
        if (approvalChanged || statusChanged) {
          // Use explicit admin approval APIs so backend sets `approval_status`
          if (after.approvalStatus === 'approved' || after.status === 'active') {
            const tierToSet = after.tier || after.type || 'megajob';
            calls.push(apiClient.approveAdminJob(after.id || after._id, tierToSet));
          } else if (after.approvalStatus === 'rejected' || after.status === 'rejected') {
            calls.push(apiClient.rejectAdminJob(after.id || after._id, 'Rejected by admin'));
          } else {
            // Fallback to moderation endpoint for other transitions
            const action = (after.status === 'active') ? 'approve' : 'reject';
            calls.push(apiClient.moderateJob(after.id || after._id, action));
          }
          continue;
        }
        if (tierChanged) {
          calls.push(apiClient.updateAdminJobTier(after.id || after._id, after.tier));
        }
        const companyObj = companies.find((c: any) => c.name === after.company);
        const reqPayload = Array.isArray(after.requirements)
          ? after.requirements
          : (typeof after.requirements === 'string'
            ? after.requirements.split('\n').map((r: string) => r.trim()).filter(Boolean)
            : undefined);
        const updatePayload: any = {
          title: after.title,
          job_title: after.title,
          description: after.description,
          job_description: after.description,
          requirements: reqPayload,
          location: after.location,
          job_location: after.location,
          company_id: companyObj?._id || companyObj?.id,
          company: after.company,
          employment_type: after.employment_type,
          type: after.employment_type,
          experience_level: after.experience_level || after.experience,
          experience: after.experience_level || after.experience,
          tier: after.tier || after.type,
          featured: after.featured,
          license_required: !!(after.license_required ?? after.licenseRequired),
          deadline: after.deadline,
          desired_candidate: (after as any).desired_candidate ?? (after as any).desiredCandidate,
          // Persist cover image URL when present (support both cases)
          cover_image_url: (after as any).cover_image_url || (after as any).coverImageUrl
        };
        // Include salary fields when available to keep database in sync
        const toNum = (v: any) => {
          const n = parseInt(String(v ?? '').replace(/[^0-9]/g, ''));
          return Number.isFinite(n) ? n : undefined;
        };
        const sType = (after as any).salary_type || (after as any).salaryType;
        if (sType) updatePayload.salary_type = sType;
        const sMin = toNum((after as any).salary_min ?? (after as any).salaryMin);
        const sMax = toNum((after as any).salary_max ?? (after as any).salaryMax);
        if (sMin !== undefined) updatePayload.salary_min = sMin;
        if (sMax !== undefined) updatePayload.salary_max = sMax;
        if ((after as any).salary_currency) updatePayload.salary_currency = (after as any).salary_currency;
        if ((after as any).pay_type || (after as any).payType) updatePayload.pay_type = (after as any).pay_type || (after as any).payType;
        if ((after as any).salary_range || typeof (after as any).salary === 'string') {
          updatePayload.salary_range = (after as any).salary_range || (after as any).salary;
        }
        calls.push(apiClient.updateJobByAdmin(after.id || after._id, updatePayload));
      }

      const results = await Promise.allSettled(calls);
      if (results.some(r => r.status === 'rejected')) {
        toast.error('Some job updates failed to save. Please retry.');
      } else if (results.length > 1) {
        toast.success('Job updates saved successfully');
      }
      // Refresh from server to ensure IDs and statuses are up-to-date
      try {
        const refreshed = await apiClient.getAdminJobs({ page: 1, limit: 2000 });
        const list = Array.isArray(refreshed?.jobs) ? refreshed.jobs : (Array.isArray(refreshed) ? refreshed : []);
        setAdminJobs(list.map(normalizeJob));
      } catch {
        // If refresh fails, keep optimistic state
      }
    } catch { /* noop */ }
  };

  if (isLoading) {
    return <Loader fullScreen text="Loading Dashboard..." />;
  }

  return (
    <>
      {ConfirmDialog}
      <NewAdminPanel
        jobs={Array.isArray(adminJobs) ? adminJobs : []}
        users={Array.isArray(users) ? users : []}
        applications={Array.isArray(adminApplications) && adminApplications.length ? adminApplications : (Array.isArray(applications) ? applications : [])}
        companies={Array.isArray(companies) ? companies : []}
        onJobUpdate={handleJobUpdate}
        onUserUpdate={setUsers}
        onCompanyUpdate={handleCompanyUpdate}
        currentUser={currentUser}
        onBackToWebsite={handleBackToWebsite}
        onLogout={handleLogout}
      />
    </>
  );
}
